import {
  ListButton,
  RefreshButton,
  Show,
  useModal,
} from "@refinedev/antd";

import { Button, Space, Tag, Row, Col } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  ShoppingCartOutlined,
  FileTextOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import { IOrder } from "interfaces";
import React from "react";
import { getStatusTagColor } from "constants/tagColors";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
  ShowModalItem,
} from "components/show";
import dayjs from "dayjs";

export const OrderShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IOrder>();
  const record = result;

  const {
    modalProps: modalPropsOrderItems,
    show: showOrderItems,
    close: closeOrderItems,
  } = useModal();
  const {
    modalProps: modalPropsOrderErrorMessages,
    show: showOrderErrorMessages,
    close: closeOrderErrorMessages,
  } = useModal();

  const hasOrderItems = Array.isArray(record?.orderItems) && (record?.orderItems?.length ?? 0) > 0;
  const hasErrorMessages = Array.isArray(record?.errorMessages) && (record?.errorMessages?.length ?? 0) > 0;

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active order",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="order">Orders</ListButton>
            <RefreshButton resource="order" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          <Button type="default" onClick={showOrderItems} disabled={!hasOrderItems}>
            Order Items
          </Button>
          <Button type="default" onClick={showOrderErrorMessages} disabled={!hasErrorMessages}>
            Error Messages
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Order Overview */}
        <ShowSection
          title="Order Overview"
          subtitle="Primary order information"
          icon={<ShoppingCartOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="EMR Reference" value={record?.reference} type="code" copyable />
            <ShowField
              label="Order Date"
              value={record?.orderDate ? dayjs(record.orderDate).format("DD-MMM-YYYY") : undefined}
            />
            <ShowField
              label="Order Status"
              value={record?.orderStatus}
              type="tag"
              tagColor={getStatusTagColor(record?.orderStatus || "")}
            />
          </ShowFieldGrid>
        </ShowSection>

        <Row gutter={24}>
          {/* UKHO Details */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="UKHO Details"
              subtitle="UK Hydrographic Office reference"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="UKHO Order ID" value={record?.ukhoOrderId} type="code" copyable />
                <ShowField label="Purchase Order Number" value={record?.purchaseOrderNumber} type="code" copyable />
              </ShowFieldGrid>
            </ShowSection>
          </Col>

          {/* Pricing */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Pricing"
              subtitle="Order value information"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Currency" value={record?.currency} type="tag" tagColor="blue" />
                <ShowField label="Total Price" value={record?.totalPrice} type="currency" />
              </ShowFieldGrid>
            </ShowSection>
          </Col>
        </Row>
      </Space>

      {/* Order Items Modal */}
      <ShowModal
        title="Order Items"
        subtitle={`${record?.orderItems?.length || 0} items`}
        icon={<FileTextOutlined />}
        open={modalPropsOrderItems.open ?? false}
        onClose={closeOrderItems}
        empty={!hasOrderItems}
        emptyText="No items in this order"
        width={700}
      >
        {record?.orderItems?.map((orderItem, index) => (
          <ShowModalItem
            key={index}
            title={`${orderItem?.productId} - ${orderItem?.productName}`}
            showDivider={index < (record?.orderItems?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="Product ID" value={orderItem?.productId} type="code" copyable />
              <ShowField label="Product Name" value={orderItem?.productName} />
              <ShowField label="Product Group" value={orderItem?.productGroup} />
              <ShowField label="Currency" value={orderItem?.currency} type="tag" tagColor="blue" />
              <ShowField label="Guide Price" value={orderItem?.guidePrice} type="currency" />
              <ShowField label="Quantity" value={orderItem?.quantity} type="number" />
              <ShowField label="Period (Months)" value={orderItem?.periodInMonths} type="number" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Error Messages Modal */}
      <ShowModal
        title="Order Error Messages"
        subtitle={`${record?.errorMessages?.length || 0} errors`}
        icon={<WarningOutlined />}
        open={modalPropsOrderErrorMessages.open ?? false}
        onClose={closeOrderErrorMessages}
        empty={!hasErrorMessages}
        emptyText="No error messages"
        width={600}
      >
        {record?.errorMessages?.map((errorMsg, index) => (
          <ShowModalItem
            key={index}
            title={`Error ${index + 1}`}
            showDivider={index < (record?.errorMessages?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={1}>
              <ShowField label="Error Message" value={errorMsg?.errorMessage} />
              <ShowField label="Error Source" value={errorMsg?.errorSource} type="tag" tagColor="red" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>
    </Show>
  );
};
